import { Alert, Box, IconButton, InputAdornment, List, Stack, TextField, Typography } from "@mui/material"
import { useMemo, useState } from "react"
import { DialogBox } from "lib/@components/dialogBox"
import { Repeat } from "lib/@components/repeat"
import Iconify from "minimals-template/components/Iconify"
import { If } from "lib/@components/switch"
import { SendToLegacyBlockingScheduleRow } from "./send-to-legacy-blocking-schedule-row"
import { compareByCode } from "authoring/lib/compare-by-code"

export function SendToLegacyBlockersDialog({
    blockingSchedules = [],
    alreadyPublishedSchedules = [],
    scheduleCount = 0,
    approvedCount = 0,
    actionLabel = "Send approved drafts",
    actionVerb = "sent",
    noApprovedTitle = "No approved drafts to send",
    emptyMessage = "Add at least one schedule before sending this work package to Legacy.",
    blockersMessage = "The following drafts are not approved and will not be sent.",
}) {
    const [search, setSearch] = useState("")
    const filteredBlockingSchedules = useMemo(filterBlockingSchedules, [blockingSchedules, search])
    const blockingCount = blockingSchedules.length
    const alreadyPublishedCount = alreadyPublishedSchedules.length
    const canSend = approvedCount > 0
    const title = dialogTitle({ scheduleCount, approvedCount, blockingCount, alreadyPublishedCount, noApprovedTitle })
    const blockingCountSummary = `${blockingCount} of ${scheduleCount} ${"schedule".pluralize(scheduleCount)} not yet approved.`
    const alreadyPublishedSummary = `${alreadyPublishedCount} new ${"schedule".pluralize(alreadyPublishedCount)} already published to Legacy and will not be ${actionVerb} again.`

    return (
        <DialogBox
            title={<Typography variant="h5">{title}</Typography>}
            value={canSend}
            okCaption={actionLabel}
            cancelCaption={canSend ? "Cancel" : "Close"}
            data-cy="send-to-legacy-blockers-dialog"
            sx={{
                "& .MuiDialogContent-root": {
                    overflow: "hidden",
                },
            }}
        >
            <Stack spacing={2}>
                <If truthy={!scheduleCount}>
                    <Alert severity="warning" icon={<Iconify icon={"material-symbols:warning-rounded"} />}>
                        <Typography variant="subtitle1" data-cy="send-to-legacy-empty-message">
                            {emptyMessage}
                        </Typography>
                    </Alert>
                </If>

                <If truthy={blockingCount > 0}>
                    <Stack spacing={2}>
                        <Alert
                            severity="warning"
                            icon={<Iconify icon={"material-symbols:warning-rounded"} />}
                            sx={{
                                alignItems: "flex-start",
                                "& .MuiAlert-icon": { display: "flex", alignItems: "center", mt: 0.25 },
                            }}
                        >
                            <Typography variant="subtitle1" data-cy="send-to-legacy-blockers-message">
                                {blockersMessage}
                            </Typography>
                        </Alert>
                        <TextField
                            data-cy="blocking-schedules-search"
                            placeholder="Search blocking schedules"
                            value={search}
                            onChange={handleSearchChange}
                            size="small"
                            fullWidth
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Iconify icon="eva:search-fill" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="Clear search"
                                                edge="end"
                                                size="small"
                                                onClick={clearSearch}
                                                disabled={!search}
                                            >
                                                <Iconify icon="material-symbols:close-rounded" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <Typography variant="body2" color="text.secondary" data-cy="blocking-schedules-count">
                            {blockingCountSummary}
                        </Typography>
                        <List
                            data-cy="blocking-schedules-list"
                            disablePadding
                            sx={{
                                maxHeight: "32vh",
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                overflowY: "auto",
                                overflowX: "hidden",
                                overscrollBehavior: "contain",
                            }}
                        >
                            <Repeat
                                list={filteredBlockingSchedules}
                                item={<SendToLegacyBlockingScheduleRow />}
                                pass="schedule"
                                fallback={
                                    <Box px={2} py={3}>
                                        <Typography variant="body2" color="text.secondary">
                                            No blocking schedules match your search.
                                        </Typography>
                                    </Box>
                                }
                            />
                        </List>
                    </Stack>
                </If>

                <If truthy={alreadyPublishedCount > 0}>
                    <Stack spacing={2}>
                        <Alert severity="info" icon={<Iconify icon={"material-symbols:info-rounded"} />}>
                            <Typography variant="subtitle1" data-cy="already-published-message">
                                {alreadyPublishedSummary}
                            </Typography>
                        </Alert>
                        <List
                            data-cy="already-published-schedules-list"
                            disablePadding
                            sx={{
                                maxHeight: "32vh",
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                overflowY: "auto",
                                overflowX: "hidden",
                                overscrollBehavior: "contain",
                            }}
                        >
                            <Repeat
                                list={[...alreadyPublishedSchedules].sort(compareByCode)}
                                item={<SendToLegacyBlockingScheduleRow />}
                                pass="schedule"
                            />
                        </List>
                    </Stack>
                </If>
            </Stack>
        </DialogBox>
    )

    function filterBlockingSchedules() {
        const searchTerm = search.trim().toLowerCase()
        if (!searchTerm) return [...blockingSchedules].sort(compareByCode)

        return blockingSchedules.filter(matchesSearchTerm).sort(compareByCode)

        function matchesSearchTerm({ code, title, status, isDraft }) {
            const searchableStatus = isDraft ? status : "no draft"
            return [code, title, searchableStatus].some((value) => value?.toLowerCase?.().includes(searchTerm))
        }
    }

    function handleSearchChange({ target }) {
        setSearch(target.value)
    }

    function clearSearch() {
        setSearch("")
    }
}

function dialogTitle({ scheduleCount, approvedCount, blockingCount, alreadyPublishedCount, noApprovedTitle }) {
    if (!scheduleCount) return "This work package is empty"
    if (approvedCount > 0) {
        if (blockingCount > 0) return "Some drafts aren't approved yet"
        return "Some schedules were already published"
    }
    if (blockingCount > 0) return noApprovedTitle
    if (alreadyPublishedCount > 0) return "All schedules already published to Legacy"
    return noApprovedTitle
}
